import dayjs from 'dayjs'
import omit from 'lodash/omit'
import { processAPIResponse } from '@/utils/request'
import { selectDateFilter } from '@/models/date'
import { saveChartData } from '@/utils/functions'
import {
  fetchCallBackChartData,
  fetchFunnelChartData,
  fetchPerformanceChartData,
  fetchRoiFunnelChartData,
  fetchTeamReportTableData
} from '@/services/api/leadAnalytics'
import * as PreferencesSelectors from '@/selectors/preferencesSelectors'
import { FILTER_PLACES } from '@/components/Filters/LeadFilters'

// The reports' date range lives in the place filters (set by the report
// toolbar's PreferencesRangePicker, so saved views capture it). The raw
// `date` object ({start, end, quickDiff} in ms) never goes to the API —
// it's mapped to the endpoint's from/to keys here. An empty date (cleared
// picker) means all-time: no date params at all.
export const toDateRangeQuery = (filters: Record<string, any> = {}, fromKey: string, toKey: string) => {
  const query = omit(filters, ['date'])
  if (filters?.date?.start) query[fromKey] = dayjs(filters.date.start).format('YYYY-MM-DD')
  if (filters?.date?.end) query[toKey] = dayjs(filters.date.end).format('YYYY-MM-DD')
  return query
}

const initialState = {
  performance: {},
  callback: {},
  funnel: {},
  teamPerformance: {},
  roiFunnel: {}
}
export default {
  namespace: 'leadsAnalytics',

  state: initialState,

  effects: {
    * fetchPerformanceChartData({ payload = {} }: { payload }, { call, put, select }) {
      const filters = yield select(state => PreferencesSelectors.selectFilters(state, FILTER_PLACES.LEAD_PERFORMANCE_REPORT))
      const query = {
        ...toDateRangeQuery(filters, 'created_from', 'created_to'),
        metrics: 'revenue,count,conversion_rate'
      }
      const { response, data } = yield call(fetchPerformanceChartData, { url: payload.url, query })
      yield processAPIResponse({ response, data }, {
        * onSuccess(data) {
          yield put({
            type: 'setPerformanceChartData',
            payload: data
          })
        },
        * onError(error) {
        }
      })
    },
    * fetchFunnelChartData({ payload = {} }: { payload }, { call, put, select }) {
      const filters = yield select(state => PreferencesSelectors.selectFilters(state, FILTER_PLACES.LEAD_FUNNEL_REPORT))
      const query = toDateRangeQuery(filters, 'created_from', 'created_to')
      if (payload?.breakdownBy)
        query['group_by'] = payload?.breakdownBy
      const { response, data } = yield call(fetchFunnelChartData, { url: payload.url, query })
      yield processAPIResponse({ response, data }, {
        * onSuccess(data) {
          yield put({
            type: 'setFunnelChartData',
            payload: data
          })
        }
      })
    },
    * fetchTeamReportTableData({ payload = {} }: { payload }, { call, put, select }) {
      const filters = yield select(state => PreferencesSelectors.selectFilters(state, FILTER_PLACES.LEAD_TEAM_PERFORMANCE))
      const query = toDateRangeQuery(filters, 'created_from', 'created_to')
      const { response, data } = yield call(fetchTeamReportTableData, { url: payload.url, query })
      yield processAPIResponse({ response, data }, {
        * onSuccess(data) {
          yield put({
            type: 'setTeamReportTableData',
            payload: data
          })
        }
      })
    },
    * fetchRoiFunnelChartData({ payload = {} }: { payload }, { call, put, select }) {
      const date = yield select(selectDateFilter)
      const chartFilters = { ...yield select(state => PreferencesSelectors.selectFilters(state, FILTER_PLACES.LEAD_ROI_FUNNEL_REPORT)) }

      const query = {
        breakdown_by: payload?.breakdownBy,
        created_from: date.start_date,
        created_to: date.end_date
      }
      const { response, data } = yield call(fetchRoiFunnelChartData, { url: payload.url, query })
      yield processAPIResponse({ response, data }, {
        * onSuccess(data) {
          yield put({
            type: 'setRoiFunnelChartData',
            payload: data
          })
        }
      })
    },
    * fetchCallBackChartData({ payload = {} }: { payload }, { call, put, select }) {
      const filters = yield select(state => PreferencesSelectors.selectFilters(state, FILTER_PLACES.LEAD_CALL_BACK_REPORT))
      const query = toDateRangeQuery(filters, 'create_at_from', 'create_at_to')
      const { response, data } = yield call(fetchCallBackChartData, { url: payload.url, query })
      yield processAPIResponse({ response, data }, {
        * onSuccess(data) {
          yield put({
            type: 'setCallbackChartData',
            payload: data
          })
        },
        * onError(error) {
        }
      })
    }


  },

  reducers: {
    setPerformanceChartData(state, action) {
      return saveChartData(state, 'performance', action.payload)
    },
    setFunnelChartData(state, action) {
      return saveChartData(state, 'funnel', action.payload)
    },
    setCallbackChartData(state, action) {
      return saveChartData(state, 'callback', action.payload)
    },
    setRoiFunnelChartData(state, action) {
      return saveChartData(state, 'roiFunnel', action.payload)
    },
    setTeamReportTableData(state, action) {
      return saveChartData(state, 'teamPerformance', action.payload)
    },
    reset() {
      return initialState
    }
  }
}
